import PropTypes from "prop-types";
import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { getEmployeeDetails, getEmployeeDetailsByEmployeeId, updateEmployeeDetails, updateEmployeeDetailsByEmployeeId } from "requests/employee";
import MCard from "components/Shared/MCard.tsx";
import { MSpinner } from "components/Shared";
import { DATA_TYPE_PHONE_NUMBER, DATA_TYPE_PERCENT, DATA_TYPE_MONEY, DATA_TYPE_DATE } from "typeConstants/dataTypes";
import { userHasRight } from "selectors/rights";
import {
  EMPLOYEE_TRAINING_EDIT,
  EMPLOYEE_CONTACT_FULL_VIEW,
  EMPLOYEE_MAC_ADJUSTMENT_EDIT,
  EMPLOYEE_MAC_ADJUSTMENT_VIEW,
  EMPLOYEE_PAY_RATE_EDIT,
  EMPLOYEE_PAY_RATE_VIEW,
  EMPLOYEE_START_DATE_EDIT,
} from "typeConstants/rights";

const EmployeeDetails = ({
    canEditEmployeePhone,
    internetUserId,
    employeeId,
    canViewEmployeeInfoFull,
    canViewMacAdjustment,
    canEditMacAdjustment,
    canViewEmployeePayRate,
    canEditEmployeePayRate,
    canEditStartDate }) => {
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [fetchingEmployeeDetails, setFetchingEmployeeDetails] = useState(false);
  const [fields, setFields] = useState([]);
  const mounted = useRef(true);

  //use these instead of the imported raw functions so this control can support either internetUserId or employeeId
  const getEmployee = callback => (employeeId ? getEmployeeDetailsByEmployeeId : getEmployeeDetails)(employeeId || internetUserId, callback, mounted);
  const updateEmployee = (fields, onSuccess) => (employeeId ? updateEmployeeDetailsByEmployeeId : updateEmployeeDetails)(employeeId || internetUserId, fields, onSuccess, mounted);
  const employeeEditFinished = editFields => {
    if (editFields.length > 0) updateEmployee(editFields, getEmployeeDetailsWrapped);
  };

  const getCardFields = (details) => {
    let _fields = [
      { key: "firstName", label: "First Name", value: details.firstName},
      { key: "lastName", label: "Last Name", value: details.lastName},
      { key: "workPhone", label: "Work Phone", dataType: DATA_TYPE_PHONE_NUMBER, editable: canEditEmployeePhone, value: details.workPhone },
      { key: "email", label: "Email", value: details.email  },
      { key: "hireDate", label: "Hire Date" , dataType: DATA_TYPE_DATE, value: details.hireDate  },
      { key: "startDate", label: "Start Date" , dataType: DATA_TYPE_DATE, value: details.startDate, editable: canEditStartDate  },
      { key: "role.name", label: "Role", value: details.role?.name},
    ];

    if(canViewEmployeeInfoFull){
      _fields.push(
        { key: "address", label: "Address", value: details.address },
        { key: "city", label: "City", value: details.city },
        { key: "state", label: "State", value: details.state },
        { key: "postalCode", label: "Zip", value: details.postalCode },
        { key: "homePhone", label: "Phone", dataType: DATA_TYPE_PHONE_NUMBER, value: details.homePhone },
      );
    }

    if(canViewEmployeePayRate){
      _fields.push(
          { key: "salary", label: "Hourly Pay Rate", dataType: DATA_TYPE_MONEY, value: details.salary, editable: canEditEmployeePayRate},
      );
    }

    if (canViewMacAdjustment) {
      _fields.push(
        { key: "macAdjustmentDollar", label: "Mac Additional $", dataType: DATA_TYPE_MONEY, editable: canEditMacAdjustment, tooltip: { title: "Money added to default pay rate for MAC Calculation", _fields }, value: details.macAdjustmentDollar},
        { key: "macAdjustmentPercent", label: "Mac Additional %", dataType: DATA_TYPE_PERCENT, editable: canEditMacAdjustment, tooltip: { title: "Percent of rate added to original MAC Calculation", _fields }, value: details.macAdjustmentPercent },
      );
    }

    setFields(_fields);
  };

  const getEmployeeDetailsWrapped = () => {
    setFetchingEmployeeDetails(true);
    getEmployee(details => {
      setEmployeeDetails(details);
      setFetchingEmployeeDetails(false);
      getCardFields(details);
    }, mounted);
  };

  useEffect(getEmployeeDetailsWrapped, [internetUserId, employeeId]);

  if (!employeeDetails || fetchingEmployeeDetails) {
    return <MSpinner />;
  }

  return (
    <MCard
      title="HR information"
      fields={fields.map(f => ({ editable: false, ...f, value: f.value}))}
      isEditable={fields.map(t => ({ editable: false, ...t, value: t.value})).some(f => f.editable)}
      onEditFinished={employeeEditFinished}
    />
  );
};

//only one of internetUserId or employeeId is needed, if both are for some reason provided employeeId is used
//if you have both to provide, definitely use employeeId, not all employees have internet user ids
EmployeeDetails.propTypes = {
  internetUserId: PropTypes.number,
  employeeId: PropTypes.number,
  canEditEmployeePhone: PropTypes.bool.isRequired,
  canViewEmployeeInfoFull: PropTypes.bool,
  canViewMacAdjustment: PropTypes.bool.isRequired,
  canEditMacAdjustment: PropTypes.bool.isRequired,
  canViewEmployeePayRate: PropTypes.bool.isRequired,
  canEditEmployeePayRate: PropTypes.bool.isRequired,
  canEditStartDate: PropTypes.bool.isRequired,
};

const mapStateToProps = state => {
  return {
    canEditEmployeePhone: userHasRight(EMPLOYEE_TRAINING_EDIT)(state),
    canViewEmployeeInfoFull: userHasRight(EMPLOYEE_CONTACT_FULL_VIEW)(state),
    canViewMacAdjustment: userHasRight(EMPLOYEE_MAC_ADJUSTMENT_VIEW)(state),
    canEditMacAdjustment: userHasRight(EMPLOYEE_MAC_ADJUSTMENT_EDIT)(state),
    canViewEmployeePayRate: userHasRight(EMPLOYEE_PAY_RATE_VIEW)(state),
    canEditEmployeePayRate: userHasRight(EMPLOYEE_PAY_RATE_EDIT)(state),
    canEditStartDate: userHasRight(EMPLOYEE_START_DATE_EDIT)(state),
  };
};

export default connect(mapStateToProps)(EmployeeDetails);
